import Header from "@commons/header";
import Column from "@components/column";
import Fields from "@components/fields";
import DropDown from "@components/fields/dropdown";
import Row from "@components/row";
import Typography from "@components/typography";
import { Form, Formik } from "formik";
import { Clock, Plus, Store, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  StyleCoverWrapper,
  StyleDayWrapper,
  StyleImageContainer,
  StyleProfileWrapper,
} from "./style";
import DeliveryTime from "@components/fields/deliverytime";
import { useModal } from "@context/ModalContext";
import theme from "@utils/theme";
import Button from "@components/button";

import CardHeader from "@commons/businesscardHeader";

import ButtonWrapper from "@components/wrapper/button-wrapper";
import { Module, StoreType, Subscription, Zone } from "@apis/index";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@store/index";
import ModuleDropdown from "@commons/modules/dropdown";
import { Store as StoreApi } from "@apis/index";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FieldWrapper from "@components/wrapper/field-wrapper";
import StoreAvailability from "@components/store-availability";

import { Helper } from "@utils/index";
import useDebounce from "@hooks/useDebounce";
import styled from "@emotion/styled";
import * as Yup from "yup";

const parseLatLngFromUrl = (
  url: string,
): { lat: string; lng: string } | null => {
  if (!url) return null;
  // Match @latitude,longitude
  const atMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (atMatch) {
    return { lat: atMatch[1], lng: atMatch[2] };
  }
  // Match q=latitude,longitude, ll=latitude,longitude, or query=latitude,longitude
  const qMatch = url.match(/[?&](q|ll|query)=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (qMatch) {
    return { lat: qMatch[2], lng: qMatch[3] };
  }
  // Match /place/Name/@latitude,longitude
  const placeMatch = url.match(/\/place\/(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (placeMatch) {
    return { lat: placeMatch[1], lng: placeMatch[2] };
  }
  return null;
};

const getPhoneValidationRules = (countryCode: number) => {
  switch (countryCode) {
    case 977: // Nepal
      return {
        formats: [
          {
            length: 10,
            validPrefixes: ["98", "97", "96", "95"],
            message:
              "Nepal mobile numbers must be 10 digits and start with 98, 97, 96, or 95",
          },
          {
            length: 8,
            validPrefixes: ["1", "2", "3", "4", "5", "6", "7", "8", "98", "97"],
            message: "Nepal landline or CDMA numbers must be 8 digits",
          },
        ],
      };
    case 91: // India
      return {
        formats: [
          {
            length: 10,
            validPrefixes: ["6", "7", "8", "9"],
            message:
              "India mobile numbers must be 10 digits and start with 6, 7, 8, or 9",
          },
        ],
      };
    case 1: // USA/Canada
      return {
        formats: [
          {
            length: 10,
            validPrefixes: null,
            message: "Phone number must be 10 digits",
          },
        ],
      };
    default:
      return {
        formats: [
          {
            length: 10,
            validPrefixes: null,
            message: "Phone number must be at least 10 digits",
          },
        ],
      };
  }
};

// Validate phone number format based on country code
const validatePhoneFormat = (
  phoneNumber: string,
  countryCode: number,
): { isValid: boolean; message?: string } => {
  if (!phoneNumber) {
    return { isValid: false, message: "Phone number is required" };
  }

  // Extract digits only (remove country code and formatting)
  const digits = phoneNumber.replace(/\D/g, "");

  // Remove country code if present
  const countryCodeStr = countryCode.toString();
  let phoneDigits = digits;
  if (digits.startsWith(countryCodeStr)) {
    phoneDigits = digits.substring(countryCodeStr.length);
  }

  // Strip leading zero if present (common in local dialing but not international format)
  if (phoneDigits.startsWith("0")) {
    phoneDigits = phoneDigits.substring(1);
  }

  const rules = getPhoneValidationRules(countryCode);
  const possibleFormats = rules.formats;

  // Check if any of the possible formats match
  const matchingFormat = possibleFormats.find((format) => {
    // Check length
    if (phoneDigits.length !== format.length) return false;

    // Check prefix if required
    if (format.validPrefixes && format.validPrefixes.length > 0) {
      return format.validPrefixes.some((prefix) =>
        phoneDigits.startsWith(prefix),
      );
    }

    return true;
  });

  if (matchingFormat) {
    return { isValid: true };
  }

  // If no format matches, provide a combined message or the message of the most likely format
  // For simplicity, if Nepal, show a specific composite message
  if (countryCode === 977) {
    return {
      isValid: false,
      message:
        "Invalid Nepal number. Use 10 digits for mobile (start with 9) or 8 digits for landline (start with 1-8).",
    };
  }

  return {
    isValid: false,
    message: possibleFormats[0]?.message || "Invalid phone number format",
  };
};

const ValidationMessage = styled.div<{ isValid?: boolean }>`
  font-size: 12px;
  margin-top: 4px;
  color: ${(props) => (props.isValid ? "#10b981" : "#ef4444")};
`;

const EmailSuggestions = styled.div`
  margin-top: 8px;
  padding: 8px;
  background-color: #f3f4f6;
  border-radius: 6px;
  font-size: 12px;
`;

const SuggestionItem = styled.div`
  padding: 4px 0;
  cursor: pointer;
  color: #3b82f6;
  &:hover {
    text-decoration: underline;
  }
  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const CommissionManager: React.FC<{
  values: any;
  setFieldValue: (field: string, value: any) => void;
  modules: any[];
  isEdit: boolean;
}> = ({ values, setFieldValue, modules, isEdit }) => {
  const [prevValues, setPrevValues] = useState<{
    moduleId: string;
    plan: string;
  }>({
    moduleId: values.moduleId,
    plan: values.plan,
  });

  useEffect(() => {
    // Only auto-fill if:
    // 1. Not in edit mode
    // 2. Plan is Commission ("1")
    // 3. Either moduleId changed or plan changed to "1"
    const moduleIdChanged = values.moduleId !== prevValues.moduleId;
    const planSwitchedToCommission =
      values.plan === "1" && prevValues.plan !== "1";

    if (
      !isEdit &&
      values.plan === "1" &&
      (moduleIdChanged || planSwitchedToCommission)
    ) {
      if (values.moduleId) {
        const selectedModule = Array.isArray(modules)
          ? modules.find((m: any) => String(m.id) === String(values.moduleId))
          : null;

        const commission =
          selectedModule?.moduleWisePercentage?.value ??
          selectedModule?.moduleWisePercentage;

        const type = selectedModule?.moduleWisePercentage?.type || "percentage";

        if (commission !== undefined && commission !== null) {
          setFieldValue("commission", commission);
          setFieldValue("commissionType", type);
        }
      }
    }

    setPrevValues({ moduleId: values.moduleId, plan: values.plan });
  }, [values.moduleId, values.plan, modules, isEdit, setFieldValue]);

  return null;
};

const StoreForm: React.FC = () => {
  const { state } = useLocation();
  const { id } = useParams<{ id: string }>();
  const [fetchedStore, setFetchedStore] = useState<any>(null);

  const isEdit = !!id;
  const storeData = state || fetchedStore;

  const { data: storeTypes } = useSelector(
    (state: RootState) => state.storeTypes,
  );

  const { data: modules } = useSelector((state: RootState) => state.modules);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!modules || modules.length === 0) {
      Module.list({ state: "modules" });
    }
  }, [modules, dispatch]);

  useEffect(() => {
    if (isEdit && !state && !fetchedStore) {
      StoreApi.find({ id })
        .then((res: any) => {
          if (res) {
            setFetchedStore(res);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch store detail:", err);
        });
    }
  }, [isEdit, id, state, fetchedStore]);

  const fetchStoreTypes = useCallback(async (moduleId) => {
    await StoreType.list({
      state: "storeTypes",
      params: { moduleId },
    });
  }, []);

  useEffect(() => {
    if (storeData?.module?.id) {
      fetchStoreTypes(storeData.module.id);
    }
  }, [storeData, fetchStoreTypes]);

  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState<any>({
    moduleId: "",
    cover: [],
    profile: "",
    name: "",
    phone: "",
    email: "",
    deliverTime: "",
    ownerName: "",
    ownerPhone: "",
    ownerEmail: "",
    password: "",
    zone: "",
    storeType: [],
    url: "",
    longitude: "",
    latitude: "",
    landmark: "",
    city: "",
    map: [],
    availability: [],
    plan: "",
    businessCertificate: "",
    pan: "",
    panCertificate: "",
    subscriptionPlan: "",
    commission: "",
    commissionType: "percentage",
    bestSeller: "",
    isFeatured: "",
    isPopular: "",
    isPureVeg: "",
  });
  const { showModal } = useModal();
  const [zonePolygons, setZonePolygons] = useState<any[]>([]);
  const [otherStorePolygons, setOtherStorePolygons] = useState<any[]>([]);
  const [showAllModules, setShowAllModules] = useState(false);

  const fetchStoreGeometries = useCallback(async () => {
    try {
      const res: any = await StoreApi.getGeometries();
      if (res) {
        const mapped = res
          .filter(
            (s: any) => !storeData || String(s.id) !== String(storeData.id),
          )
          .map((s: any) => {
            let geom = s.geometry;
            if (typeof geom === "string") {
              try {
                geom = JSON.parse(geom);
              } catch (e) {
                geom = [];
              }
            }
            return {
              id: s.id,
              name: s.storeName,
              module: s.moduleName,
              email: s.storeEmail,
              phone: s.storePhoneNo,
              avatar: s.avatar,
              polygons: Array.isArray(geom)
                ? geom.map((path: any) =>
                    Array.isArray(path)
                      ? path.map(([lng, lat]: any) => ({ lat, lng }))
                      : [],
                  )
                : [],
            };
          })
          .filter((s: any) => s.polygons.length > 0);
        setOtherStorePolygons(mapped);
      }
    } catch (err) {
      console.error(err);
    }
  }, [storeData]);

  useEffect(() => {
    fetchStoreGeometries();
  }, [fetchStoreGeometries]);

  useEffect(() => {
    if (storeData) {
      setInitialValues({
        moduleId: storeData?.module?.id || "",
        cover:
          (Array.isArray(storeData?.coverImages)
            ? storeData.coverImages
                .filter((img) => img && typeof img === "object")
                .map((img) => ({
                  id: img.id,
                  url: img.coverImages,
                }))
            : []) || [],
        profile: storeData?.avatar || "",
        name: storeData?.storeName || "",
        phone: storeData?.storePhoneNo || "",
        email: storeData?.storeEmail || "",
        deliverTime: storeData?.estimatedDeliveryTime || "",
        ownerName: storeData?.name || "",
        ownerPhone: storeData?.phoneNo || "",
        ownerEmail: storeData?.email || "",
        password: "",
        storeType:
          (Array.isArray(storeData?.storeTypes)
            ? storeData.storeTypes
                .filter((s) => s && typeof s === "object")
                .map((s) => s.id)
            : []) || [],
        url: storeData?.googleMapsLink || "",
        longitude: storeData?.longitude || "",
        latitude: storeData?.latitude || "",
        landmark: "",
        city: storeData?.city || "",
        map: (() => {
          let geom = storeData?.geometry;
          if (typeof geom === "string") {
            try {
              geom = JSON.parse(geom);
            } catch (e) {
              geom = [];
            }
          }
          return Array.isArray(geom)
            ? geom.map((path: Array<[number, number]>) =>
                Array.isArray(path)
                  ? path.map(([lng, lat]) => ({ lat, lng }))
                  : [],
              )
            : [];
        })(),
        availability:
          Array.isArray(storeData?.availabilities) &&
          storeData.availabilities.length > 0
            ? storeData.availabilities
                .filter((a) => a && typeof a === "object")
                .map((a) => ({
                  day: a.day,
                  isClosed: a.isClosed,
                  is24Hours: a.is24Hour,
                  timeSlots: Array.isArray(a.slots)
                    ? a.slots
                        .filter((slot) => slot && typeof slot === "object")
                        .map((slot) => ({
                          startTime: slot.startTime,
                          endTime: slot.endTime,
                        }))
                    : [],
                }))
            : [],
        plan: storeData?.plan === "Subscription" ? "2" : "1",
        commission: storeData?.commission || "",
        commissionType:
          modules?.find(
            (m: any) => m && String(m.id) === String(storeData?.module?.id),
          )?.moduleWisePercentage?.type || "percentage",
        businessCertificate: storeData?.registrationImg || "",
        pan: storeData?.panNo || "",
        panCertificate: storeData?.panImg || "",
        zone: storeData?.zone?.id || "",
        subscriptionPlan: storeData?.subscription?.id || "",
        bestSeller: storeData?.bestSeller || false,
        isFeatured: storeData?.isFeatured || false,
        isPopular: storeData?.isPopular || false,
        isPureVeg: storeData?.isPureVeg || false,
      });
    }
  }, [storeData, modules]);
  const { data: zones } = useSelector((state: RootState) => state.zones);
  const { items: subscriptions } = useSelector(
    (state: RootState) => state.subscriptions,
  );
  const fetchZones = useCallback(async (moduleId?: number) => {
    await Zone.list({
      state: "zones",
      params: { moduleId: moduleId || undefined },
    })
      .then(() => {})
      .catch(() => {});
  }, []);

  // Fetch zone geometry when editing an existing store
  useEffect(() => {
    if (storeData?.zone?.id) {
      Zone.find({
        id: storeData.zone.id,
      }).then(async (zone) => {
        if (zone && zone.geometry) {
          const mappedGeometry = zone.geometry.map((path) =>
            path.map(([lng, lat]) => ({ lat, lng })),
          );
          setZonePolygons(mappedGeometry);
        }
      });
    }
  }, [storeData]);

  const fetchSubscriptions = useCallback(async (moduleId) => {
    await Subscription.list({ state: "subscriptions", params: { moduleId } })
      .then(() => {})
      .catch(() => {});
  }, []);
  useEffect(() => {
    fetchZones(storeData?.module?.id);
    if (storeData?.module?.id) {
      fetchSubscriptions(storeData.module.id);
    }
  }, [storeData, fetchZones, fetchSubscriptions]);
  const user = Helper.getUser();

  // Validation state for store fields
  const [emailValidation, setEmailValidation] = useState<{
    message: string;
    isValid: boolean;
    suggestions?: string[];
  } | null>(null);
  const [phoneValidation, setPhoneValidation] = useState<{
    message: string;
    isValid: boolean;
  } | null>(null);

  // Validation state for owner fields
  const [ownerEmailValidation, setOwnerEmailValidation] = useState<{
    message: string;
    isValid: boolean;
    suggestions?: string[];
  } | null>(null);
  const [ownerPhoneValidation, setOwnerPhoneValidation] = useState<{
    message: string;
    isValid: boolean;
  } | null>(null);

  const [isValidating, setIsValidating] = useState(false);
  const [isValidatingOwner, setIsValidatingOwner] = useState(false);

  // Track current form values for validation (store)
  const [currentEmail, setCurrentEmail] = useState<string>("");
  const [currentPhone, setCurrentPhone] = useState<string>("");
  const [selectedCountryCode, setSelectedCountryCode] = useState<{
    callingCode: number;
    code: string;
  }>({ callingCode: 977, code: "NP" }); // Default to Nepal

  // Track current form values for validation (owner)
  const [currentOwnerEmail, setCurrentOwnerEmail] = useState<string>("");
  const [currentOwnerPhone, setCurrentOwnerPhone] = useState<string>("");
  const [selectedOwnerCountryCode, setSelectedOwnerCountryCode] = useState<{
    callingCode: number;
    code: string;
  }>({ callingCode: 977, code: "NP" }); // Default to Nepal

  // Debounced values for validation (store)
  const debouncedEmail = useDebounce(currentEmail, 800);
  const debouncedPhone = useDebounce(currentPhone, 800);

  // Debounced values for validation (owner)
  const debouncedOwnerEmail = useDebounce(currentOwnerEmail, 800);
  const debouncedOwnerPhone = useDebounce(currentOwnerPhone, 800);

  // Validation function for store fields
  const validateEmailAndPhone = useCallback(
    async (email?: string, phone?: string) => {
      if (!email && !phone) {
        setEmailValidation(null);
        setPhoneValidation(null);
        return;
      }

      setIsValidating(true);
      try {
        const params: any = {};
        if (email) params.storeEmail = email;
        if (phone) params.storePhoneNo = phone;

        const response = await StoreApi.checkEmailAndPhone({ params });

        if (response?.email) {
          setEmailValidation({
            message: response.email.message,
            isValid: !response.email.exists,
            suggestions: response.email.suggestions || [],
          });
        }

        if (response?.phone) {
          setPhoneValidation({
            message: response.phone.message,
            isValid: !response.phone.exists,
          });
        }
      } catch (err: any) {
        console.error("Validation error:", err);
        // Set error state
        if (email) {
          setEmailValidation({
            message: "Error checking email availability",
            isValid: false,
          });
        }
        if (phone) {
          setPhoneValidation({
            message: "Error checking phone availability",
            isValid: false,
          });
        }
      } finally {
        setIsValidating(false);
      }
    },
    [],
  );

  // Validation function for owner fields
  const validateOwnerEmailAndPhone = useCallback(
    async (email?: string, phone?: string) => {
      if (!email && !phone) {
        setOwnerEmailValidation(null);
        setOwnerPhoneValidation(null);
        return;
      }

      setIsValidatingOwner(true);
      try {
        const params: any = {};
        if (email) params.email = email;
        if (phone) params.phoneNo = phone;

        const response = await StoreApi.checkOwnerEmailAndPhone({ params });

        if (response?.email) {
          setOwnerEmailValidation({
            message: response.email.message,
            isValid: !response.email.exists,
            suggestions: response.email.suggestions || [],
          });
        }

        if (response?.phone) {
          setOwnerPhoneValidation({
            message: response.phone.message,
            isValid: !response.phone.exists,
          });
        }
      } catch (err: any) {
        console.error("Owner validation error:", err);
        // Set error state
        if (email) {
          setOwnerEmailValidation({
            message: "Error checking email availability",
            isValid: false,
          });
        }
        if (phone) {
          setOwnerPhoneValidation({
            message: "Error checking phone availability",
            isValid: false,
          });
        }
      } finally {
        setIsValidatingOwner(false);
      }
    },
    [],
  );

  // Store current phone value for validation
  const [currentPhoneValue, setCurrentPhoneValue] = useState<string>("");
  const [currentOwnerPhoneValue, setCurrentOwnerPhoneValue] =
    useState<string>("");

  // Validate on debounced values change
  useEffect(() => {
    if (!isEdit) {
      const emailToCheck = debouncedEmail?.trim() || undefined;
      // Don't strip +, only strip spaces/dashes etc if needed.
      // Keeping + ensures we match numbers stored with + in DB.
      const rawPhone = debouncedPhone
        ? debouncedPhone.replace(/[^0-9+]/g, "")
        : undefined;
      // Ensure phone has + prefix and country code
      let phoneToCheck = rawPhone;
      if (rawPhone) {
        if (!rawPhone.startsWith("+")) {
          const callingCodeStr = selectedCountryCode.callingCode.toString();
          // if rawPhone doesn't start with calling code, prepend it
          if (!rawPhone.startsWith(callingCodeStr)) {
            phoneToCheck = `+${callingCodeStr}${rawPhone}`;
          } else {
            phoneToCheck = `+${rawPhone}`;
          }
        }
      }

      if (emailToCheck || phoneToCheck) {
        // Validate email if it contains @ (user is typing email)
        const shouldValidateEmail =
          emailToCheck && emailToCheck.includes("@") && emailToCheck.length > 3;

        // Validate phone format first, then check availability
        let shouldValidatePhone = false;
        // Get pure digits for length check
        const digitsOnly = phoneToCheck ? phoneToCheck.replace(/\D/g, "") : "";

        if (phoneToCheck && currentPhoneValue) {
          const formatCheck = validatePhoneFormat(
            currentPhoneValue,
            selectedCountryCode.callingCode,
          );
          shouldValidatePhone = formatCheck.isValid && digitsOnly.length >= 10;

          // If format is invalid, show format error
          if (digitsOnly.length >= 10 && !formatCheck.isValid) {
            setPhoneValidation({
              message: formatCheck.message || "Invalid phone number format",
              isValid: false,
            });
          }
        } else if (phoneToCheck && digitsOnly.length >= 10) {
          // If we have 10 digits but no full phone value yet, still try to validate
          shouldValidatePhone = true;
        }

        if (shouldValidateEmail || shouldValidatePhone) {
          validateEmailAndPhone(
            shouldValidateEmail ? emailToCheck : undefined,
            shouldValidatePhone ? phoneToCheck : undefined,
          );
        } else if (emailToCheck || phoneToCheck) {
          // Clear validation if format is not ready for validation
          if (!shouldValidateEmail && emailToCheck) {
            setEmailValidation(null);
          }
          if (!shouldValidatePhone && phoneToCheck && digitsOnly.length < 10) {
            setPhoneValidation(null);
          }
        }
      } else {
        // Clear validation if both are empty
        setEmailValidation(null);
        setPhoneValidation(null);
      }
    }
  }, [
    debouncedEmail,
    debouncedPhone,
    isEdit,
    validateEmailAndPhone,
    currentPhoneValue,
    selectedCountryCode.callingCode,
  ]);

  // Validate owner fields on debounced values change
  // Validate owner fields on debounced values change
  useEffect(() => {
    if (!isEdit) {
      const emailToCheck = debouncedOwnerEmail?.trim() || undefined;
      // Keep + for syncing with DB storage
      const rawPhone = debouncedOwnerPhone
        ? debouncedOwnerPhone.replace(/[^0-9+]/g, "")
        : undefined;
      // Ensure phone has + prefix and country code
      let phoneToCheck = rawPhone;
      if (rawPhone) {
        if (!rawPhone.startsWith("+")) {
          const callingCodeStr =
            selectedOwnerCountryCode.callingCode.toString();
          if (!rawPhone.startsWith(callingCodeStr)) {
            phoneToCheck = `+${callingCodeStr}${rawPhone}`;
          } else {
            phoneToCheck = `+${rawPhone}`;
          }
        }
      }

      if (emailToCheck || phoneToCheck) {
        const shouldValidateEmail =
          emailToCheck && emailToCheck.includes("@") && emailToCheck.length > 3;

        let shouldValidatePhone = false;
        // Get pure digits for length check
        const digitsOnly = phoneToCheck ? phoneToCheck.replace(/\D/g, "") : "";

        if (phoneToCheck && currentOwnerPhoneValue) {
          const formatCheck = validatePhoneFormat(
            currentOwnerPhoneValue,
            selectedOwnerCountryCode.callingCode,
          );
          shouldValidatePhone = formatCheck.isValid && digitsOnly.length >= 10;

          if (digitsOnly.length >= 10 && !formatCheck.isValid) {
            setOwnerPhoneValidation({
              message: formatCheck.message || "Invalid phone number format",
              isValid: false,
            });
          }
        } else if (phoneToCheck && digitsOnly.length >= 10) {
          shouldValidatePhone = true;
        }

        if (shouldValidateEmail || shouldValidatePhone) {
          validateOwnerEmailAndPhone(
            shouldValidateEmail ? emailToCheck : undefined,
            shouldValidatePhone ? phoneToCheck : undefined,
          );
        } else if (emailToCheck || phoneToCheck) {
          if (!shouldValidateEmail && emailToCheck) {
            setOwnerEmailValidation(null);
          }
          if (!shouldValidatePhone && phoneToCheck && digitsOnly.length < 10) {
            setOwnerPhoneValidation(null);
          }
        }
      } else {
        setOwnerEmailValidation(null);
        setOwnerPhoneValidation(null);
      }
    }
  }, [
    debouncedOwnerEmail,
    debouncedOwnerPhone,
    isEdit,
    validateOwnerEmailAndPhone,
    currentOwnerPhoneValue,
    selectedOwnerCountryCode.callingCode,
  ]);

  // Reset validation when editing existing store or update current values
  useEffect(() => {
    if (isEdit) {
      setEmailValidation(null);
      setPhoneValidation(null);
      setOwnerEmailValidation(null);
      setOwnerPhoneValidation(null);
      setCurrentEmail("");
      setCurrentPhone("");
      setCurrentOwnerEmail("");
      setCurrentOwnerPhone("");
    } else {
      const email = initialValues.email || "";
      const phone = initialValues.phone
        ? initialValues.phone.replace(/\D/g, "")
        : "";
      const ownerEmail = initialValues.ownerEmail || "";
      const ownerPhone = initialValues.ownerPhone
        ? initialValues.ownerPhone.replace(/\D/g, "")
        : "";
      setCurrentEmail(email);
      setCurrentPhone(phone);
      setCurrentOwnerEmail(ownerEmail);
      setCurrentOwnerPhone(ownerPhone);
    }
  }, [
    isEdit,
    initialValues.email,
    initialValues.phone,
    initialValues.ownerEmail,
    initialValues.ownerPhone,
  ]);

  const handleSubmit = async (values) => {
    const formData: any = new FormData();
    console.log({ values });
    if (values.cover.length > 0) {
      let i = 0;
      values.cover.forEach((file: any) => {
        if (file.rawFile) {
          formData.append(`coverImages[${i}]`, file.rawFile);
          i++;
        }
      });
    }

    if (values.profile?.rawFile)
      formData.append("avatar", values.profile.rawFile);

    if (values.businessCertificate?.rawFile)
      formData.append("registrationImg", values.businessCertificate.rawFile);

    if (values.panCertificate?.rawFile)
      formData.append("panImg", values.panCertificate.rawFile);
    if (!isEdit) formData.append("moduleId", values.moduleId || "");
    formData.append("name", values.ownerName || "");
    if (!isEdit) formData.append("phoneNo", values.ownerPhone || "");
    if (!isEdit) formData.append("email", values.ownerEmail || "");
    formData.append("storeName", values.name || "");
    formData.append("storePhoneNo", values.phone || "");
    if (!isEdit) formData.append("storeEmail", values.email || "");
    formData.append("estimatedDeliveryTime", values.deliverTime || "");

    if (values.storeType && values.storeType.length > 0) {
      let i = 0;
      values.storeType.forEach((typeId: number) => {
        formData.append(`typeIds[${i}]`, typeId);
        i++;
      });
    } else {
      formData.append("typeIds", "");
    }
    formData.append("googleMapsLink", values.url || "");
    formData.append("latitude", values.latitude || "");
    formData.append("longitude", values.longitude || "");
    formData.append("city", values.city || "");
    formData.append("panNo", values.pan || "");
    formData.append("zoneId", values.zone);
    formData.append(
      "plan",
      values.plan === "1" ? "Commission" : "Subscription",
    );

    if (values.plan === "1" && values.commission) {
      formData.append("commission", values.commission);
    }
    if (values.password) formData.append("password", values.password);

    if (values.plan === "2" && values.subscriptionPlan)
      formData.append("subscriptionId", values.subscriptionPlan);

    formData.append("bestSeller", values.bestSeller ? "true" : "false");
    formData.append("isFeatured", values.isFeatured ? "true" : "false");
    formData.append("isPopular", values.isPopular ? "true" : "false");
    formData.append("isPureVeg", values.isPureVeg ? "true" : "false");

    // Always append geometry if it exists in values, ensuring updates are saved
    if (Array.isArray(values.map) && values.map.length > 0) {
      const geometryPayload = values.map.map((path) =>
        path.map((point) => [point.lng, point.lat]),
      );
      formData.append("geometry", JSON.stringify(geometryPayload));
    }

    if (Array.isArray(values.availability) && values.availability.length > 0) {
      const availabilitiesPayload = values.availability.map((a) => ({
        day: a.day,
        isClosed: !!a.isClosed,
        is24Hour: !!a.is24Hours,
        slots: Array.isArray(a.timeSlots)
          ? a.timeSlots.map((slot) => ({
              startTime: slot.startTime,
              endTime: slot.endTime,
            }))
          : [],
      }));

      formData.append("availabilities", JSON.stringify(availabilitiesPayload));
    }
    if (isEdit) {
      await StoreApi.update({
        state: "stores",
        data: formData,
        id: storeData?.id || id,
      })
        .then((res) => {
          if (user.role === "store") {
            window.location.reload();
          } else navigate("/admin/users/store/store-lists");
        })
        .catch((err) => {});
    } else
      await StoreApi.create({ state: "stores", data: formData })
        .then((res) => {
          if (user.role === "store") {
            window.location.reload();
          } else navigate("/admin/users/store/store-lists");
        })
        .catch((err) => {});
  };

  // Yup validation schema - only validate phone/email when creating new store
  // Recreate schema when country code changes
  const validationSchema = useMemo(() => {
    if (isEdit) return undefined;

    return Yup.object().shape({
      phone: Yup.string()
        .required("Mobile number is required")
        .test("phone-format", function (value) {
          if (!value) return false;
          // Access country code from closure
          const formatCheck = validatePhoneFormat(
            value,
            selectedCountryCode.callingCode,
          );
          if (!formatCheck.isValid) {
            return this.createError({
              message: formatCheck.message || "Invalid phone number format",
            });
          }
          return true;
        })
        .test(
          "phone-availability",
          "Phone number already exists",
          async (value) => {
            if (!value) return true;

            // First check format
            const formatCheck = validatePhoneFormat(
              value,
              selectedCountryCode.callingCode,
            );
            if (!formatCheck.isValid) {
              return true; // Let format validation handle this
            }

            // Check if phone validation exists and is invalid
            if (phoneValidation && !phoneValidation.isValid) {
              return false;
            }
            return true;
          },
        ),
      email: Yup.string()
        .email("Please enter a valid email address")
        .required("Email is required")
        .test("email-availability", "Email already exists", async (value) => {
          if (!value) return true;

          // Check if email validation exists and is invalid
          if (emailValidation && !emailValidation.isValid) {
            return false;
          }
          return true;
        }),
      // Owner fields validation
      ownerPhone: Yup.string()
        .required("Owner mobile number is required")
        .test("owner-phone-format", function (value) {
          if (!value || isEdit) return true; // Skip format check on update since it's disabled
          const formatCheck = validatePhoneFormat(
            value,
            selectedOwnerCountryCode.callingCode,
          );
          if (!formatCheck.isValid) {
            return this.createError({
              message: formatCheck.message || "Invalid phone number format",
            });
          }
          return true;
        })
        .test(
          "owner-phone-availability",
          "Phone number already exists",
          async (value) => {
            if (!value || isEdit) return true; // Skip availability check on update

            const formatCheck = validatePhoneFormat(
              value,
              selectedOwnerCountryCode.callingCode,
            );
            if (!formatCheck.isValid) {
              return true;
            }

            if (ownerPhoneValidation && !ownerPhoneValidation.isValid) {
              return false;
            }
            return true;
          },
        ),
      ownerEmail: Yup.string()
        .email("Please enter a valid email address")
        .required("Owner email is required")
        .test(
          "owner-email-availability",
          "Email already exists",
          async (value) => {
            if (!value || isEdit) return true; // Skip availability check on update

            if (ownerEmailValidation && !ownerEmailValidation.isValid) {
              return false;
            }
            return true;
          },
        ),
    });
  }, [
    isEdit,
    selectedCountryCode.callingCode,
    phoneValidation,
    emailValidation,
    selectedOwnerCountryCode.callingCode,
    ownerPhoneValidation,
    ownerEmailValidation,
  ]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ handleSubmit, setFieldValue, values, isSubmitting }) => {
        return (
          <Form>
            <CommissionManager
              values={values}
              setFieldValue={setFieldValue}
              modules={modules as any[]}
              isEdit={isEdit}
            />
            <Column>
              {/* Header */}
              <Row styleRow="justify-content:space-between;align-items:center">
                <Header
                  title={isEdit ? "Update Store" : "Add New Store"}
                  icon={Store}
                />
              </Row>
              {/* Store Information */}
              <FieldWrapper>
                <Column>
                  <Typography weight={600} size={18}>
                    Store Information
                  </Typography>
                  <StyleImageContainer>
                    <StyleCoverWrapper>
                      <Fields.Upload
                        accept="image/*,video/*"
                        name="cover"
                        useRawFile={true}
                        multiple
                        maxFiles={5}
                        size={100}
                        placeholder="Upload images or videos"
                        onRemoveImage={async (file) => {
                          await StoreApi.removeCoverImage({
                            id: file.id,
                            state: "stores",
                          });
                        }}
                      />
                    </StyleCoverWrapper>
                    <StyleProfileWrapper>
                      <Fields.Upload
                        name="profile"
                        variant="profile"
                        size={120}
                        useRawFile={true}
                      />
                    </StyleProfileWrapper>
                  </StyleImageContainer>
                  <Row>
                    <Fields.Input
                      name="name"
                      placeholder="Enter Store Name"
                      label="Store Name"
                    />
                    <div id="phone-field-wrapper" style={{ width: "100%" }}>
                      <Fields.PhoneInput
                        name="phone"
                        label="Mobile Number"
                        placeholder="Enter Mobile Number"
                        disabled={false}
                        onChange={(value) => {
                          setFieldValue("phone", value);
                          if (!isEdit) {
                            const phoneDigits = value
                              ? value.replace(/[^0-9+]/g, "")
                              : "";
                            setCurrentPhone(phoneDigits);
                            setCurrentPhoneValue(value || "");
                            setPhoneValidation(null);
                          }
                        }}
                        onChangeCode={(codes) => {
                          setSelectedCountryCode(codes);

                          if (!isEdit) {
                            setPhoneValidation(null);
                          }
                        }}
                      />
                      {!isEdit && phoneValidation && (
                        <ValidationMessage
                          id="phone-validation-message"
                          isValid={phoneValidation.isValid}
                        >
                          {phoneValidation.message}
                        </ValidationMessage>
                      )}
                      {!isEdit && isValidating && !phoneValidation && (
                        <ValidationMessage id="phone-validation-loading">
                          Checking availability...
                        </ValidationMessage>
                      )}
                    </div>
                  </Row>
                  <Row>
                    <div id="email-field-wrapper" style={{ width: "100%" }}>
                      <Fields.Input
                        name="email"
                        type="email"
                        placeholder="Enter Mail"
                        label="Email"
                        disabled={isEdit}
                        onChange={(value) => {
                          setFieldValue("email", value);
                          if (!isEdit) {
                            setCurrentEmail(value || "");
                            setEmailValidation(null);
                          }
                        }}
                      />
                      {!isEdit && emailValidation && (
                        <>
                          <ValidationMessage
                            id="email-validation-message"
                            isValid={emailValidation.isValid}
                          >
                            {emailValidation.message}
                          </ValidationMessage>
                          {emailValidation.suggestions &&
                            emailValidation.suggestions.length > 0 && (
                              <EmailSuggestions id="email-suggestions">
                                <div
                                  id="email-suggestions-label"
                                  style={{
                                    marginBottom: "4px",
                                    fontWeight: 600,
                                  }}
                                >
                                  Suggested alternatives:
                                </div>
                                {emailValidation.suggestions.map(
                                  (suggestion, index) => (
                                    <SuggestionItem
                                      key={index}
                                      id={`email-suggestion-${index}`}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => {
                                        setFieldValue("email", suggestion);
                                        validateEmailAndPhone(
                                          suggestion,
                                          values.phone,
                                        );
                                      }}
                                      onKeyDown={(
                                        e: React.KeyboardEvent<HTMLDivElement>,
                                      ) => {
                                        if (
                                          e.key === "Enter" ||
                                          e.key === " "
                                        ) {
                                          e.preventDefault();
                                          setFieldValue("email", suggestion);
                                          validateEmailAndPhone(
                                            suggestion,
                                            values.phone,
                                          );
                                        }
                                      }}
                                    >
                                      {suggestion}
                                    </SuggestionItem>
                                  ),
                                )}
                              </EmailSuggestions>
                            )}
                        </>
                      )}
                      {!isEdit && isValidating && !emailValidation && (
                        <ValidationMessage id="email-validation-loading">
                          Checking availability...
                        </ValidationMessage>
                      )}
                    </div>
                    <DeliveryTime
                      name="deliverTime"
                      label="Estimate Delivery Duration"
                      placeholder="20 - 25 mins"
                    />
                  </Row>

                  <Row styleRow="align-items:center">
                    <ModuleDropdown
                      styleInput="width:97%"
                      onChange={async (val) => {
                        setFieldValue("moduleId", val);
                        setFieldValue("zone", "");

                        await fetchStoreTypes(val);
                        await fetchSubscriptions(val);
                        await fetchZones(val);
                      }}
                      disabled={isEdit}
                    />

                    <DropDown
                      isMultiple
                      name="storeType"
                      placeholder="---Select Store Type---"
                      label="Type"
                      options={storeTypes || []}
                      valueAsKey="id"
                      titleAsKey="name"
                      styleInput="width:97.3%"
                      styleOptionsWrapper="width:100%"
                    />
                  </Row>
                </Column>
              </FieldWrapper>
              {/* Owner Details */}
              <FieldWrapper>
                <Column>
                  <Typography weight={600} size={18}>
                    Owner Details
                  </Typography>
                  <Row>
                    <Fields.Input
                      name="ownerName"
                      placeholder="Enter Full Name"
                      label="Owner Name"
                    />
                    <div
                      id="owner-phone-field-wrapper"
                      style={{ width: "100%" }}
                    >
                      <Fields.PhoneInput
                        name="ownerPhone"
                        label="Mobile Number"
                        placeholder="Enter Mobile Number"
                        disabled={isEdit}
                        onChange={(value) => {
                          setFieldValue("ownerPhone", value);
                          if (!isEdit) {
                            // Keep + for syncing with DB storage
                            const phoneDigits = value
                              ? value.replace(/[^0-9+]/g, "")
                              : "";
                            setCurrentOwnerPhone(phoneDigits);
                            setCurrentOwnerPhoneValue(value || "");
                            setOwnerPhoneValidation(null);
                          }
                        }}
                        onChangeCode={(codes) => {
                          setSelectedOwnerCountryCode(codes);
                          if (!isEdit) {
                            setOwnerPhoneValidation(null);
                          }
                        }}
                      />
                      {!isEdit && ownerPhoneValidation && (
                        <ValidationMessage
                          id="owner-phone-validation-message"
                          isValid={ownerPhoneValidation.isValid}
                        >
                          {ownerPhoneValidation.message}
                        </ValidationMessage>
                      )}
                      {!isEdit &&
                        isValidatingOwner &&
                        !ownerPhoneValidation && (
                          <ValidationMessage id="owner-phone-validation-loading">
                            Checking availability...
                          </ValidationMessage>
                        )}
                    </div>
                  </Row>
                  <Row>
                    <div
                      id="owner-email-field-wrapper"
                      style={{ width: "100%" }}
                    >
                      <Fields.Input
                        name="ownerEmail"
                        type="email"
                        placeholder="Enter Email"
                        label="Email"
                        disabled={isEdit}
                        onChange={(value) => {
                          setFieldValue("ownerEmail", value);
                          if (!isEdit) {
                            setCurrentOwnerEmail(value || "");
                            setOwnerEmailValidation(null);
                          }
                        }}
                      />
                      {!isEdit && ownerEmailValidation && (
                        <>
                          <ValidationMessage
                            id="owner-email-validation-message"
                            isValid={ownerEmailValidation.isValid}
                          >
                            {ownerEmailValidation.message}
                          </ValidationMessage>
                          {ownerEmailValidation.suggestions &&
                            ownerEmailValidation.suggestions.length > 0 && (
                              <EmailSuggestions id="owner-email-suggestions">
                                <div
                                  id="owner-email-suggestions-label"
                                  style={{
                                    marginBottom: "4px",
                                    fontWeight: 600,
                                  }}
                                >
                                  Suggested alternatives:
                                </div>
                                {ownerEmailValidation.suggestions.map(
                                  (suggestion, index) => (
                                    <SuggestionItem
                                      key={index}
                                      id={`owner-email-suggestion-${index}`}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => {
                                        setFieldValue("ownerEmail", suggestion);
                                        validateOwnerEmailAndPhone(
                                          suggestion,
                                          values.ownerPhone,
                                        );
                                      }}
                                      onKeyDown={(
                                        e: React.KeyboardEvent<HTMLDivElement>,
                                      ) => {
                                        if (
                                          e.key === "Enter" ||
                                          e.key === " "
                                        ) {
                                          e.preventDefault();
                                          setFieldValue(
                                            "ownerEmail",
                                            suggestion,
                                          );
                                          validateOwnerEmailAndPhone(
                                            suggestion,
                                            values.ownerPhone,
                                          );
                                        }
                                      }}
                                    >
                                      {suggestion}
                                    </SuggestionItem>
                                  ),
                                )}
                              </EmailSuggestions>
                            )}
                        </>
                      )}
                      {!isEdit &&
                        isValidatingOwner &&
                        !ownerEmailValidation && (
                          <ValidationMessage id="owner-email-validation-loading">
                            Checking availability...
                          </ValidationMessage>
                        )}
                    </div>
                    <Fields.Password
                      name="password"
                      placeholder={
                        isEdit
                          ? "Leave blank to keep current"
                          : "Enter Password"
                      }
                      label={isEdit ? "Password (optional)" : "Password"}
                    />
                  </Row>
                </Column>
              </FieldWrapper>
              {/* Store Address */}
              <FieldWrapper>
                <Column>
                  <Typography weight={600} size={18}>
                    Store Address
                  </Typography>
                  <Row styleRow="flex-direction:column">
                    <div style={{ width: "100%", flex: 1.5 }}>
                      <Column>
                        <Row>
                          <DropDown
                            name="zone"
                            placeholder="---Select Zone---"
                            label="Zone"
                            options={Array.isArray(zones) ? zones : []}
                            titleAsKey="name"
                            valueAsKey="id"
                            styleInput="width:97.3%"
                            styleOptionsWrapper="width:100%"
                            disabled={!values.moduleId && !isEdit}
                            onChange={async (val) => {
                              setFieldValue("zone", val);
                              await Zone.find({
                                id: val,
                              }).then(async (zone) => {
                                if (zone.geometry) {
                                  const mappedGeometry = zone.geometry.map(
                                    (path) =>
                                      path.map(([lng, lat]) => ({ lat, lng })),
                                  );
                                  setZonePolygons(mappedGeometry);
                                  // setFieldValue("map", mappedGeometry); // Don't overwrite map
                                  setTimeout(() => {}, 400);
                                } else {
                                  setZonePolygons([]);
                                }
                                fetchZones(values.moduleId);
                              });
                            }}
                          />
                          <Fields.Input
                            name="url"
                            placeholder="URL"
                            label="Store Google Map"
                            onChange={(value) => {
                              setFieldValue("url", value);
                              const parsed = parseLatLngFromUrl(value);
                              if (parsed) {
                                setFieldValue("latitude", parsed.lat);
                                setFieldValue("longitude", parsed.lng);
                              }
                            }}
                          />
                        </Row>

                        <Row>
                          <Fields.Input
                            name="latitude"
                            placeholder="Ex:98.4342"
                            label="Latitude"
                          />
                          <Fields.Input
                            name="longitude"
                            placeholder="Ex:98.54463"
                            label="Longitude"
                          />
                          <Fields.Input
                            name="city"
                            placeholder="Enter City/Town"
                            label="City/Town"
                          />
                        </Row>
                      </Column>
                    </div>
                    <br />
                    <div
                      style={{ width: "100%", flex: 2, position: "relative" }}
                    >
                      {(() => {
                        const selectedModule = Array.isArray(modules)
                          ? modules.find(
                              (m: any) =>
                                String(m.id) === String(values.moduleId),
                            )
                          : null;

                        // Strict check: ONLY "grocery"
                        const isGrocery =
                          selectedModule?.name?.toLowerCase() === "grocery";

                        return (
                          <>
                            {isGrocery && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "10px",
                                  right: "50px",
                                  zIndex: 10,
                                  backgroundColor: "white",
                                  padding: "8px 12px",
                                  borderRadius: "4px",
                                  boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  setShowAllModules(!showAllModules)
                                }
                              >
                                <input
                                  type="checkbox"
                                  checked={showAllModules}
                                  onChange={() => {}} // Handled by parent div
                                  style={{ cursor: "pointer" }}
                                />
                                <span
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    userSelect: "none",
                                  }}
                                >
                                  Show All Modules
                                </span>
                              </div>
                            )}
                            <Fields.Map
                              key={values.zone}
                              name="map"
                              height="550px"
                              backgroundPolygons={zonePolygons}
                              otherStores={
                                isGrocery
                                  ? showAllModules
                                    ? otherStorePolygons
                                    : otherStorePolygons.filter(
                                        (store) =>
                                          store.module?.toLowerCase() ===
                                          "grocery",
                                      )
                                  : []
                              }
                              allowDrawing={!!values.zone}
                              selectedPoint={
                                values.latitude &&
                                values.longitude &&
                                !isNaN(Number(values.latitude)) &&
                                !isNaN(Number(values.longitude))
                                  ? {
                                      lat: Number(values.latitude),
                                      lng: Number(values.longitude),
                                    }
                                  : null
                              }
                              onSelectPoint={
                                values.zone
                                  ? (point, mapsUrl) => {
                                      setFieldValue(
                                        "longitude",
                                        point?.lng || "",
                                      );
                                      setFieldValue(
                                        "latitude",
                                        point?.lat || "",
                                      );
                                      setFieldValue("url", mapsUrl || "");
                                    }
                                  : undefined
                              }
                            />
                          </>
                        );
                      })()}
                    </div>
                  </Row>
                </Column>
              </FieldWrapper>
              <FieldWrapper>
                <Column>
                  <Row styleRow="align-items:center;justify-content:space-between">
                    <Typography weight={600} size={18}>
                      Store Availability
                    </Typography>
                  </Row>

                  <StoreAvailability
                    value={values.availability}
                    onChange={(schedules) =>
                      setFieldValue("availability", schedules)
                    }
                  />
                </Column>
              </FieldWrapper>
              <FieldWrapper>
                <Column>
                  <Typography weight={600} size={18}>
                    Choose Business Plan
                  </Typography>
                  <Fields.GroupRadioBox
                    name="plan"
                    options={[
                      { id: 1, title: "Commision Base" },
                      { id: 2, title: "Subscription Base" },
                    ]}
                    titleAsKey="title"
                    valueAsKey="id"
                  />
                  <Row styleRow="margin-top: 15px; grid-gap: 20px;">
                    <div
                      style={{
                        flex: 1,
                        opacity: values.plan !== "1" ? 0.6 : 1,
                      }}
                    >
                      <Fields.Input
                        name="commission"
                        label={`Commission (${values.commissionType === "flat" ? "Flat" : "%"})`}
                        placeholder="Enter Commission"
                        type="number"
                        disabled={values.plan !== "1"}
                      />
                    </div>
                    <div
                      style={{
                        flex: 1,
                        opacity: values.plan !== "2" ? 0.6 : 1,
                      }}
                    >
                      <DropDown
                        name="subscriptionPlan"
                        placeholder="---Select Subscription---"
                        label="Subscription"
                        options={
                          Array.isArray(subscriptions) ? subscriptions : []
                        }
                        titleAsKey="name"
                        valueAsKey="id"
                        styleInput="width:97.3%"
                        styleOptionsWrapper="width:100%"
                        disabled={values.plan !== "2"}
                        onChange={(val) =>
                          setFieldValue("subscriptionPlan", val)
                        }
                      />
                      {values.plan === "2" &&
                        values.subscriptionPlan &&
                        (() => {
                          console.log(
                            "Sub Check:",
                            values.subscriptionPlan,
                            subscriptions,
                          );
                          const sub = Array.isArray(subscriptions)
                            ? subscriptions.find(
                                (s: any) =>
                                  String(s.id) ===
                                  String(values.subscriptionPlan),
                              )
                            : null;
                          if (!sub) return null;
                          return (
                            <div
                              style={{
                                marginTop: 10,
                                padding: 12,
                                background: "#f8f9fa",
                                borderRadius: 6,
                                border: "1px solid #e9ecef",
                                fontSize: 13,
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600,
                                  marginBottom: 4,
                                  color: theme.colors.primary,
                                }}
                              >
                                {sub.name}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  marginBottom: 4,
                                }}
                              >
                                <span>Price:</span>
                                <span style={{ fontWeight: 500 }}>
                                  {sub.price}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  marginBottom: 4,
                                }}
                              >
                                <span>Validity:</span>
                                <span style={{ fontWeight: 500 }}>
                                  {sub.validityInDays} Days
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span>Order Limit:</span>
                                <span style={{ fontWeight: 500 }}>
                                  {sub.isUnlimited
                                    ? "Unlimited"
                                    : sub.orderLimit}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                    </div>
                  </Row>

                  <Row styleRow="margin-top:20px; gap:20px">
                    <Fields.Switch name="bestSeller" label="Best Seller" />
                    <Fields.Switch name="isFeatured" label="Featured" />
                    <Fields.Switch name="isPopular" label="Popular" />
                    {modules
                      ?.find(
                        (m: any) => String(m.id) === String(values.moduleId),
                      )
                      ?.name?.toLowerCase() === "food" && (
                      <Fields.Switch name="isPureVeg" label="Pure Veg" />
                    )}
                  </Row>

                  {values.plan === "1" && (
                    <CardHeader
                      stylecardheader="border-radius:12px;font-weight:200;font-size:14px"
                      title={`Vendor will pay ${values.commission || "0"}${values.commissionType === "flat" ? "" : "%"} commission to Zedfo from each order. You will get access of all the features and options in vendor panel, app and interaction with user`}
                    />
                  )}
                </Column>
              </FieldWrapper>
              <FieldWrapper>
                <Column>
                  <Typography weight={600} size={18}>
                    Upload Documents
                  </Typography>
                  <Fields.Input
                    name="pan"
                    label="PAN Number"
                    placeholder="12345"
                  />
                  <Row>
                    {" "}
                    <Fields.Upload
                      name="businessCertificate"
                      label="Business Registration Certificate"
                      height={220}
                      useRawFile={true}
                      accept="image/*,.pdf"
                    />
                    <Fields.Upload
                      name="panCertificate"
                      label="PAN Certificate"
                      height={220}
                      useRawFile={true}
                      accept="image/*,.pdf"
                    />
                  </Row>
                </Column>
              </FieldWrapper>
              <ButtonWrapper>
                <Button title="Reset" type="reset" variant="reset" />
                <Button
                  title={isEdit ? "Update" : "Add"}
                  type="submit"
                  loading={isSubmitting}
                />
              </ButtonWrapper>
            </Column>
          </Form>
        );
      }}
    </Formik>
  );
};

export default StoreForm;
